import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  BellRing,
  MailCheck,
  AlertTriangle,
  Users,
  UserCheck,
  Loader2,
  RefreshCw,
  Mail,
  Calendar,
  ChevronRight,
  UserRound,
} from 'lucide-react';
import { StatusBadge } from '../../../components/UI/StatusBadge';
import Modal from '../../../components/UI/Modal';

/**
 * RestDayNotificationCoverage — audit view for the rest-day compliance alerts.
 *
 * Answers HR's "were the managers actually told?" question for the 12-day
 * MOM rest-day cap, across BOTH notification channels:
 *   - Progressive daily alerts from day 9. Recipients expand from RO to HR
 *     and then Deputy HOD/HOD as the legal ceiling approaches.
 *   - Coverage gaps: at-risk workers whose reporting chain could not be
 *     resolved, so NO manager was alerted. The highest-value HR blind-spot.
 *   - Consolidated compliance digest: the periodic OT + rest-day rollup
 *     (secondary panel).
 *
 * Backed by /staff-core/rest-day-monitoring/notification-coverage/.
 *
 * Status / tier colours come exclusively from <StatusBadge> (the single
 * source of truth in statusConfig.js) — no local colour maps.
 */

const ROLE_LABELS = {
  reporting_officer: 'Reporting Officer',
  trade_leader: 'Trade Leader',
  lead_trade_leader: 'Lead Trade Leader',
  deputy_hod: 'Deputy HOD',
  hod: 'HOD',
  hr: 'HR',
};

const ESCALATION_LABELS = {
  ro_intervention: 'RO intervention',
  hr_oversight: 'HR oversight',
  hod_escalation: 'HOD escalation',
  legal_ceiling: 'Legal ceiling',
  legal_breach: 'Legal breach',
};

const LIFECYCLE_LABELS = {
  first_breach: 'First breach',
  ceiling_reached: 'Ceiling reached',
  resolved: 'Resolved',
  false_refire: 'False re-fire',
};

const ESCALATION_STEPS = [
  { day: '9', status: 'warning', label: 'Digest: RO + Trade Leader' },
  { day: '10', status: 'warning', label: 'Digest adds HR' },
  { day: '11', status: 'warning', label: 'Digest adds HOD chain' },
  { day: '12', status: 'critical', label: 'Stays in the same digest' },
  { day: '13+', status: 'violated', label: 'Immediate breach alert' },
];

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-SG', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const formatMonthLabel = (yearMonth) => {
  if (!yearMonth) return '';
  try {
    const [y, m] = yearMonth.split('-');
    const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    return d.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
  } catch (e) {
    return yearMonth;
  }
};

const groupAlertDeliveries = (deliveries) => {
  const batches = new Map();

  deliveries.forEach((delivery) => {
    const fallbackTimestamp = delivery.detectedAt?.slice(0, 16) || 'unknown';
    const key = delivery.relatedAlertId
      ? `alert-${delivery.relatedAlertId}`
      : [
        delivery.workerStaffId,
        delivery.consecutiveDays,
        delivery.escalationStage,
        fallbackTimestamp,
      ].join('|');

    if (!batches.has(key)) {
      batches.set(key, {
        id: key,
        workerStaffId: delivery.workerStaffId,
        workerName: delivery.workerName,
        department: delivery.department,
        tier: delivery.tier,
        consecutiveDays: delivery.consecutiveDays,
        escalationStage: delivery.escalationStage,
        detectedAt: delivery.detectedAt,
        recipients: [],
      });
    }

    batches.get(key).recipients.push(delivery);
  });

  return Array.from(batches.values());
};

const AlertBatchOutcome = ({ batch, onOpenRecipients }) => {
  const count = batch.recipients.length;

  return (
    <button
      type="button"
      onClick={() => onOpenRecipients(batch)}
      className="inline-flex items-center gap-1 rounded-full group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
      aria-label={`View ${count} recipients notified about ${batch.workerName || batch.workerStaffId}`}
      title="View who was notified"
    >
      <StatusBadge status="sent" label={`${count} notified`} />
      <ChevronRight className="w-3.5 h-3.5 text-green-700 opacity-70 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

const AlertBatchRecipientsModal = ({ batch, isOpen, onClose }) => {
  if (!batch) return null;

  const recipients = batch.recipients || [];
  const stageLabel = ESCALATION_LABELS[batch.escalationStage] || 'Progressive escalation';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Who was notified"
      size="lg"
      theme="admin"
      contentClassName="space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
          <BellRing className="w-5 h-5 text-red-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">
            {batch.workerName || batch.workerStaffId}
            <span className="text-gray-400 font-normal"> · </span>
            Day {batch.consecutiveDays}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            {stageLabel} reached {recipients.length} manager
            {recipients.length === 1 ? '' : 's'} / HR contact
            {recipients.length === 1 ? '' : 's'} on {formatDateTime(batch.detectedAt)}.
            Channel badges show confirmed delivery evidence.
          </p>
        </div>
      </div>

      {recipients.length > 0 ? (
        <>
          <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recipients.map((recipient) => (
              <div key={recipient.id} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {recipient.recipientName || recipient.recipientStaffId}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {recipient.recipientStaffId} · {ROLE_LABELS[recipient.recipientRole] || recipient.recipientRole}
                    </p>
                  </div>
                  <StatusBadge status={recipient.tier} />
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <StatusBadge
                    status={recipient.inAppCreated ? 'sent' : 'failed'}
                    label={recipient.inAppCreated ? 'In-app created' : 'In-app missing'}
                  />
                  <StatusBadge
                    status={recipient.emailConfirmed ? 'sent' : 'pending'}
                    label={recipient.emailConfirmed ? 'Email confirmed' : 'Email not confirmed'}
                  />
                  {recipient.whatsappExpected && (
                    <StatusBadge
                      status={recipient.whatsappConfirmed ? 'sent' : 'pending'}
                      label={recipient.whatsappConfirmed ? 'WhatsApp confirmed' : 'WhatsApp not confirmed'}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[32%]" />
                <col className="w-[22%]" />
                <col className="w-[46%]" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Recipient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600">Confirmed channels</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recipients.map((recipient) => (
                  <tr key={recipient.id} className="table-row">
                    <td className="px-4 py-3 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {recipient.recipientName || recipient.recipientStaffId}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{recipient.recipientStaffId}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {ROLE_LABELS[recipient.recipientRole] || recipient.recipientRole}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge
                          status={recipient.inAppCreated ? 'sent' : 'failed'}
                          label={recipient.inAppCreated ? 'In-app created' : 'In-app missing'}
                        />
                        <StatusBadge
                          status={recipient.emailConfirmed ? 'sent' : 'pending'}
                          label={recipient.emailConfirmed ? 'Email confirmed' : 'Email not confirmed'}
                        />
                        {recipient.whatsappExpected && (
                          <StatusBadge
                            status={recipient.whatsappConfirmed ? 'sent' : 'pending'}
                            label={recipient.whatsappConfirmed ? 'WhatsApp confirmed' : 'WhatsApp not confirmed'}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
          <UserRound className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Recipient details unavailable</h4>
          <p className="text-sm text-gray-500">
            No per-recipient delivery records were found for this escalation.
          </p>
        </div>
      )}
    </Modal>
  );
};

const RestDayNotificationCoverage = ({
  getNotificationCoverage,
  selectedMonth,
  onChangeMonth,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  // Auto-jump once from an empty current month (e.g. July Watch-only) to the
  // latest month that actually has Warning+ chain/HR deliveries (e.g. June).
  const didAutoJumpRef = useRef(false);

  const monthLabel = formatMonthLabel(selectedMonth);

  const load = useCallback(async () => {
    if (!selectedMonth) return;
    setLoading(true);
    try {
      const result = await getNotificationCoverage({ year_month: selectedMonth });
      setData(result);
    } finally {
      setLoading(false);
    }
  }, [getNotificationCoverage, selectedMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const gaps = data?.coverageGaps || [];
  const deliveries = useMemo(() => data?.alertDeliveries || [], [data?.alertDeliveries]);
  const deliveryBatches = useMemo(() => groupAlertDeliveries(deliveries), [deliveries]);
  const lifecycle = useMemo(() => data?.alertLifecycle || [], [data?.alertLifecycle]);
  const digestDeliveries = data?.digestDeliveries || [];
  const digestEnabled = data?.digestEnabled === true;
  const hasChainActivity = deliveries.length > 0 || gaps.length > 0 || lifecycle.length > 0;
  const latestActivityMonth = data?.latestActivityMonth || null;
  const latestActivityLabel = formatMonthLabel(latestActivityMonth);
  const canJumpToLatest =
    Boolean(latestActivityMonth) &&
    latestActivityMonth !== selectedMonth &&
    !hasChainActivity &&
    typeof onChangeMonth === 'function';

  // HR lands on Coverage with the page month defaulted to "this month". When
  // this month has no Warning+ chain activity but an earlier month does,
  // jump once so the table actually populates instead of looking broken.
  useEffect(() => {
    if (didAutoJumpRef.current || loading || !data) return;
    if (!canJumpToLatest) return;
    didAutoJumpRef.current = true;
    onChangeMonth(latestActivityMonth);
  }, [loading, data, canJumpToLatest, latestActivityMonth, onChangeMonth]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Notification Coverage</h3>
            <p className="text-sm text-gray-600 mt-1">
              Who has been alerted about at-risk workers in{' '}
              <span className="font-medium text-gray-700">{monthLabel}</span> — and
              which workers have no reachable manager (coverage gaps).
            </p>
            <p className="text-xs text-gray-500 mt-2 max-w-2xl">
              Tracks the daily rest-day queue from day 9 onward. Days 9–12 are one
              09:00 digest. A new day-12 clock-in later in the day is one update at
              12:00, 17:00, or 22:00. A 13th consecutive day is an immediate breach alert.
              Email counts only after webhook confirmation.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap self-start md:self-auto">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="icon-button-admin"
              aria-label="Refresh notification coverage"
              title="Refresh notification coverage"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {summary.lastDetectedAt && (
          <p className="text-xs text-gray-400 mt-3">
            Last alert dispatched: {formatDateTime(summary.lastDetectedAt)}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <BellRing className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Progressive escalation</h3>
            <p className="text-sm text-gray-600 mt-1">
              Managers receive one morning digest from day 9 through the day-12 ceiling.
              A separate email is sent only when someone works a 13th consecutive day.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {ESCALATION_STEPS.map((step) => (
            <div key={step.day} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Day {step.day}
                </p>
                <StatusBadge status={step.status} />
              </div>
              <p className="text-sm font-medium text-gray-900 mt-2">{step.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Empty current month, but history exists elsewhere — HR next step */}
      {!loading && canJumpToLatest && (
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="text-center py-4">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No chain alerts in {monthLabel}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm mx-auto">
              No worker reached the day-9 escalation threshold this month, so there is nothing to
              show here yet. The latest coverage activity is in{' '}
              <span className="font-medium text-gray-700">{latestActivityLabel}</span>.
            </p>
            <button
              type="button"
              onClick={() => onChangeMonth(latestActivityMonth)}
              className="btn btn-admin-primary mt-4"
            >
              <Calendar className="w-4 h-4" />
              View {latestActivityLabel}
            </button>
          </div>
        </div>
      )}

      {/* Summary cards — admin metrics-card pattern (DS §Metrics Cards) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <BellRing className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.alertsDispatched || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Alerts dispatched</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MailCheck className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.emailConfirmed || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Email confirmed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.recipientsNotified || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Recipients informed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.coverageGaps || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Coverage gaps</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 col-span-2 lg:col-span-1">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{summary.falseRefires || 0}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">False re-fires</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert lifecycle — first breach / resolved / false re-fire narrative */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 min-w-0">
          <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900 truncate min-w-0">Alert lifecycle</h3>
          <span className="text-sm text-gray-500 truncate flex-shrink min-w-0 hidden sm:inline">
            — first breach, resolve, and false re-fire for the same streak
          </span>
        </div>
        {lifecycle.length > 0 ? (
          <>
            <div className="md:hidden divide-y divide-gray-100">
              {lifecycle.map((row) => (
                <div key={row.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {row.workerName || row.workerStaffId}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{row.workerStaffId}</p>
                    </div>
                    <StatusBadge status={row.eventType || row.status} />
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    {LIFECYCLE_LABELS[row.eventType] || row.eventType}
                    {row.consecutiveDays != null ? ` · ${row.consecutiveDays}d` : ''}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{row.notes}</p>
                  <p className="text-xs text-gray-400 mt-2">{formatDateTime(row.at)}</p>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Event</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Days</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Notes</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lifecycle.map((row) => (
                    <tr key={row.id} className="table-row">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{row.workerName || row.workerStaffId}</p>
                        <p className="text-xs text-gray-500">{row.workerStaffId}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {LIFECYCLE_LABELS[row.eventType] || row.eventType}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <StatusBadge status={row.eventType || row.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">
                        {row.consecutiveDays ?? '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={row.notes}>
                        {row.notes || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right">
                        {formatDateTime(row.at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No ceiling or breach events</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              When a worker hits day 12 or 13+, the story (first breach → resolved → any false
              re-fire) appears here for {monthLabel}.
            </p>
          </div>
        )}
      </div>

      {/* Coverage gaps — the headline HR signal */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold text-gray-900">Coverage Gaps</h3>
          <span className="text-sm text-gray-500">
            — at-risk workers with no reachable manager
          </span>
        </div>
        {gaps.length > 0 ? (
          <>
            {/* Mobile: stacked cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {gaps.map((g) => (
                <div key={g.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{g.workerName || g.workerStaffId}</p>
                      <p className="text-xs text-gray-500 truncate">{g.workerStaffId} · {g.department || '—'}</p>
                    </div>
                    {g.tier && <div className="flex-shrink-0"><StatusBadge status={g.tier} /></div>}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2 text-xs text-gray-500">
                    <span>{g.consecutiveDays} consecutive days</span>
                    <span>{formatDateTime(g.detectedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Department</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Tier</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Consecutive days</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Detected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {gaps.map((g) => (
                    <tr key={g.id} className="table-row">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{g.workerName || g.workerStaffId}</p>
                        <p className="text-xs text-gray-500">{g.workerStaffId}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{g.department || '—'}</td>
                      <td className="px-6 py-4 text-center">
                        {g.tier ? (
                          <StatusBadge status={g.tier} />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">{g.consecutiveDays}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right">{formatDateTime(g.detectedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            {hasChainActivity ? (
              <>
                <MailCheck className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No coverage gaps</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  Every at-risk worker in {monthLabel} had at least one reachable manager.
                  Workers whose reporting chain can&apos;t be resolved would appear here.
                </p>
              </>
            ) : (
              <>
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No chain alerts this month</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  No worker reached day 9 in {monthLabel}, so no rest-day digest audience
                  was built. A separate email is logged here only for a day-13+ breach.
                  Days 7–8 remain employee-only Watch activity.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Real-time escalation activity — one row per worker alert batch */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 min-w-0">
          <BellRing className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <h3 className="text-lg font-semibold text-gray-900 truncate min-w-0">Escalation Activity</h3>
          <span className="text-sm text-gray-500 truncate flex-shrink min-w-0 hidden sm:inline">
            — click the notified count to see managers and HR reached
          </span>
        </div>
        {deliveryBatches.length > 0 ? (
          <>
            <div className="md:hidden grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
              {deliveryBatches.map((batch) => (
                <button
                  type="button"
                  key={batch.id}
                  onClick={() => setSelectedBatch(batch)}
                  className="bg-gray-50 rounded-xl p-3.5 text-left hover:bg-gray-100 transition-colors border border-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{batch.workerName || batch.workerStaffId}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {batch.workerStaffId} · {batch.department || '—'}
                      </p>
                      <p className="text-xs font-medium text-gray-600 mt-1">
                        {ESCALATION_LABELS[batch.escalationStage] || 'Progressive escalation'}
                      </p>
                    </div>
                    {batch.tier && <div className="flex-shrink-0"><StatusBadge status={batch.tier} /></div>}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-3">
                    <span className="text-[11px] text-gray-500">
                      Day {batch.consecutiveDays} · {formatDateTime(batch.detectedAt)}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600">
                      {batch.recipients.length} notified
                      <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[25%]" />
                  <col className="w-[23%]" />
                  <col className="w-[12%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Worker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Escalation</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Tier</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Recipients</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Days</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Detected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {deliveryBatches.map((batch) => (
                    <tr key={batch.id} className="table-row">
                      <td className="px-6 py-4 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{batch.workerName || batch.workerStaffId}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {batch.workerStaffId} · {batch.department || '—'}
                        </p>
                      </td>
                      <td className="px-6 py-4 min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {ESCALATION_LABELS[batch.escalationStage] || 'Progressive escalation'}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {batch.tier ? <StatusBadge status={batch.tier} /> : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <AlertBatchOutcome batch={batch} onOpenRecipients={setSelectedBatch} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium tabular-nums">{batch.consecutiveDays}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right tabular-nums">{formatDateTime(batch.detectedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            {loading ? (
              <>
                <Loader2 className="w-12 h-12 text-gray-300 mx-auto mb-4 animate-spin" />
                <p className="text-sm text-gray-500">Loading escalation activity…</p>
              </>
            ) : (
              <>
                <BellRing className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No escalation activity</h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  No worker reached day 9 in {monthLabel}, so no immediate breach alert
                  was logged. Days 9–12 are in the rest-day digest below. Employee Watch
                  reminders at days 7–8 are not listed here.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Consolidated compliance digest (secondary) */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <Mail className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Rest Day Management Digest</h3>
          <span className="text-sm text-gray-500">
            — one email per recipient for days 9–12
          </span>
        </div>
        {digestDeliveries.length > 0 ? (
          <>
            {/* Mobile: stacked cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {digestDeliveries.map((d) => (
                <div key={d.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{d.recipientName || d.recipientStaffId}</p>
                      <p className="text-xs text-gray-500 truncate">{d.recipientStaffId} · {ROLE_LABELS[d.recipientRole] || d.recipientRole}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusBadge status={d.status} label={d.status === 'pending' ? 'Enqueued' : undefined} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2 text-xs text-gray-500">
                    <span className="capitalize">{d.cadence} · {d.coveredWorkerCount} workers</span>
                    <span>{formatDateTime(d.emailConfirmedAt || d.generatedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Recipient</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600">Cadence</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Workers</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-600">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-600">Sent / Enqueued</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {digestDeliveries.map((d) => (
                    <tr key={d.id} className="table-row">
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">{d.recipientName || d.recipientStaffId}</p>
                        <p className="text-xs text-gray-500">{d.recipientStaffId}</p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{ROLE_LABELS[d.recipientRole] || d.recipientRole}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">{d.cadence}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 text-right font-medium">{d.coveredWorkerCount}</td>
                      <td className="px-6 py-4 text-center">
                        <StatusBadge
                          status={d.status}
                          label={d.status === 'pending' ? 'Enqueued' : undefined}
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 text-right">
                        {formatDateTime(d.emailConfirmedAt || d.generatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            {loading ? (
              <>
                <Loader2 className="w-12 h-12 text-gray-300 mx-auto mb-4 animate-spin" />
                <p className="text-sm text-gray-500">Loading digest deliveries…</p>
              </>
            ) : (
              <>
                <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {digestEnabled ? 'No digest deliveries' : 'Compliance digest is off'}
                </h3>
                <p className="text-sm text-gray-500 max-w-md mx-auto">
                  {digestEnabled
                    ? `No consolidated compliance digest has dispatched for ${monthLabel} yet.`
                    : 'The periodic OT + rest-day rollup email is disabled in this environment. Real-time Warning+ chain alerts above are the primary coverage signal.'}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      <AlertBatchRecipientsModal
        batch={selectedBatch}
        isOpen={Boolean(selectedBatch)}
        onClose={() => setSelectedBatch(null)}
      />
    </div>
  );
};

export default RestDayNotificationCoverage;
